/**
 * Public (ungated) citizen help for the application advisor.
 * Same content as Studio → Help → permits, without login.
 */
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Container, Link, Typography,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { resolveHelpSection, slugifyHelpHeading } from './studio/studioHelpCatalog';
import DemoSiteNav from '../components/demo/DemoSiteNav';

const flattenChildrenText = (children) => (
  React.Children.toArray(children).map((child) => {
    if (typeof child === 'string' || typeof child === 'number') return String(child);
    if (React.isValidElement(child)) return flattenChildrenText(child.props?.children);
    return '';
  }).join('')
);

const headingWithAnchor = (Tag) => ({ children }) => {
  const id = slugifyHelpHeading(flattenChildrenText(children));
  return <Tag id={id}>{children}</Tag>;
};

const markdownComponents = {
  a: ({ href, children }) => {
    const h = typeof href === 'string' ? href : '';
    if (!h) return <span>{children}</span>;
    if (/^https?:\/\//i.test(h)) {
      return <a href={h} target="_blank" rel="noopener noreferrer">{children}</a>;
    }
    if (h.startsWith('/')) return <RouterLink to={h}>{children}</RouterLink>;
    if (h.startsWith('#')) return <a href={h}>{children}</a>;
    return <span>{children}</span>;
  },
  h1: headingWithAnchor('h1'),
  h2: headingWithAnchor('h2'),
  h3: headingWithAnchor('h3'),
  h4: headingWithAnchor('h4'),
};

export default function CitizenPermitHelpPage() {
  const section = resolveHelpSection('permits');
  const md = section?.markdown || '_Help content unavailable._';

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default' }}>
      <Box sx={{
        borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', px: 2, py: 1.5,
      }}
      >
        <Container maxWidth="md" sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="overline" color="primary">Application advisor</Typography>
            <Typography variant="h6" fontWeight={700}>How this works</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <DemoSiteNav />
            <Button component={RouterLink} to="/v2/demo/permit" variant="outlined" size="small">
              Back to advisor
            </Button>
          </Box>
        </Container>
      </Box>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Guidance product — one App, city = jurisdiction package.
          {' '}
          <Link component={RouterLink} to="/v2/demo/permit?city=berkeley">Try Berkeley</Link>
          {' · '}
          <Link component={RouterLink} to="/v2/demo/permit?city=san-jose">Try San José</Link>
          {' · '}
          <Link component={RouterLink} to="/v2/demo/permit">San Francisco</Link>
        </Typography>
        <Box
          className="citizen-permit-help-md"
          sx={{
            '& h1,& h2,& h3,& h4': { scrollMarginTop: 72 },
            '& pre': { overflow: 'auto' },
            '& table': { display: 'block', overflowX: 'auto' },
          }}
        >
          <ReactMarkdown components={markdownComponents}>{md}</ReactMarkdown>
        </Box>
      </Container>
    </Box>
  );
}
